import { existsSync, lstatSync, mkdirSync, rmSync, symlinkSync } from 'node:fs';
import { join } from 'node:path';
import { SKILL_FILENAME } from './skill-version.js';

const SKILL_NAME = 'wayai';

/**
 * Self-heal Claude Code's `.claude/skills/wayai` symlink.
 *
 * The `skills` installer writes the real skill to `.agents/skills/wayai` and
 * symlinks `.claude/skills/wayai` to it — but only when `.claude/` already
 * exists, silently skipping the link otherwise (see `init.ts`). Claude Code
 * reads `.claude/skills/`, so a wiped or
 * never-created `.claude/` leaves it unable to load the skill — and the in-skill
 * self-heal can't help, since that only runs once the skill is loaded. So the
 * CLI rebuilds the link from the `.agents/` copy whenever it doesn't resolve.
 *
 * Idempotent; replaces only a broken symlink (never a real dir/file); returns
 * true only when it heals. Never throws — runs on the `wayai status` hot path.
 */
export function healClaudeSkillLink(root: string): boolean {
  try {
    // The real install must exist to link to — otherwise this is the bootstrap
    // case (no skill at all), owned by the documented install command.
    const source = join(root, '.agents', 'skills', SKILL_NAME);
    if (!existsSync(join(source, SKILL_FILENAME))) return false;

    const link = join(root, '.claude', 'skills', SKILL_NAME);
    // Already resolves (working symlink or a real install) — nothing to do.
    if (existsSync(join(link, SKILL_FILENAME))) return false;

    // Something is at the path but doesn't resolve. Only replace a (broken)
    // symlink; never clobber a real dir/file we don't own.
    let entry: ReturnType<typeof lstatSync> | null = null;
    try {
      entry = lstatSync(link);
    } catch {
      entry = null;
    }
    if (entry) {
      if (!entry.isSymbolicLink()) return false;
      rmSync(link, { force: true });
    }

    mkdirSync(join(root, '.claude', 'skills'), { recursive: true });
    // Relative target so it survives clone/move; `type: 'dir'` is a no-op on
    // posix and the correct hint on Windows.
    symlinkSync(join('..', '..', '.agents', 'skills', SKILL_NAME), link, 'dir');
    return true;
  } catch {
    // Best-effort: a failure here (e.g. Windows without symlink privilege) must
    // never break the calling command.
    return false;
  }
}

// Help/version/usage tokens carry no repo intent — skip the heal for them.
const HEAL_SKIP_COMMANDS = new Set(['help', '--help', '-h', '--version', '-v']);

/**
 * Entry-point wrapper: heal on any real CLI command, skipping help/version and
 * the no-arg usage banner. Runs from the CLI dispatch so the *first* `wayai`
 * command an agent issues repairs the link — not only `wayai status`. Returns
 * true only when it heals. Encapsulates the skip policy so it stays unit-tested
 * (`index.ts` has no test harness).
 */
export function healSkillLinkForCommand(command: string | undefined, root: string): boolean {
  if (!command || HEAL_SKIP_COMMANDS.has(command)) return false;
  return healClaudeSkillLink(root);
}
